// @flow
import * as t from './actionTypes'
import { httpGet, httpPatch, httpPost } from 'lib/http'
import { setBanner } from 'lib/notification/actions'
import Rollbar from 'lib/rollbar'

const base = (number: string) => `/admin/accounts/${number}/digital-file`

export const fetchDigitalFile = (number: string) => {
  return (dispatch: Function) => {
    return httpGet(base(number))
      .then((data) => {
        dispatch({
          type: t.SET,
          number,
          template: data.template,
          template_version: data.template_version,
          files: data.files,
          legacy: data.legacy,
          archived: data.archived,
        })
      })
      .catch((err) => {
        Rollbar.error('Error on fetchDigitalFile', err)
        dispatch(setBanner(`Error loading digital file - ${err.message || err}`, 'danger'))
      })
  }
}

export const clearDigitalFile = (number: string) => ({
  type: t.CLEAR,
  number,
})

export const selectPath = (number: string, path: ?string) => ({
  type: t.SELECT_PATH,
  number,
  path,
})

export const uploadFile = (
  number: string,
  params: { path: string, name: string, content_type: string, data: string },
) => {
  return (dispatch: Function) => {
    dispatch({ type: t.SET_UPLOADING, number, uploading: true })

    return httpPost(`${base(number)}/files`, params)
      .then((file) => {
        dispatch({ type: t.ADD_FILE, number, file })
        dispatch({ type: t.SET_UPLOADING, number, uploading: false })
        dispatch(setBanner('File uploaded'))
        return file
      })
      .catch((err) => {
        Rollbar.error('Error on uploadFile', err)
        dispatch({ type: t.SET_UPLOADING, number, uploading: false })
        dispatch(setBanner(`Error uploading file - ${err.message || err}`, 'danger'))
        throw err
      })
  }
}

export const archiveFile = (number: string, id: number) => {
  return (dispatch: Function) => {
    return httpPost(`${base(number)}/files/${id}/archive`, {})
      .then((file) => {
        dispatch({ type: t.ARCHIVE_FILE, number, file })
        dispatch(setBanner('File archived'))
      })
      .catch((err) => {
        Rollbar.error('Error on archiveFile', err)
        dispatch(setBanner(`Error archiving file - ${err.message || err}`, 'danger'))
      })
  }
}

const fetchPresignedUrl = (key: string, mode: 'preview' | 'download') =>
  httpGet(`/admin/files/${key}/${mode}`)

export const previewFile = (
  key: ?string,
  contentType: ?string,
  name: ?string,
) => {
  return () => {
    if (!key) return Promise.resolve()
    const qs = new URLSearchParams({ key })
    if (name) qs.set('name', name)
    if (contentType) qs.set('content_type', contentType)
    window.open(`/files/view?${qs.toString()}`, '_blank', 'noopener,noreferrer')
    return Promise.resolve()
  }
}

export const downloadFile = (key: ?string) => {
  return (dispatch: Function) => {
    if (!key) return Promise.resolve()
    return fetchPresignedUrl(key, 'download')
      .then(({ url }) => {
        window.location.href = url
      })
      .catch((err) => {
        Rollbar.error('Error on downloadFile', err)
        dispatch(setBanner(`Error downloading file - ${err.message || err}`, 'danger'))
      })
  }
}

export const moveFile = (number: string, id: number, path: string) => {
  return (dispatch: Function) => {
    return httpPatch(`${base(number)}/files/${id}`, { path })
      .then((file) => {
        dispatch({ type: t.MOVE_FILE, number, file })
        dispatch(setBanner('File moved'))
        return file
      })
      .catch((err) => {
        Rollbar.error('Error on moveFile', err)
        dispatch(setBanner(`Error moving file - ${err.message || err}`, 'danger'))
        throw err
      })
  }
}

export const renameFile = (number: string, id: number, name: string) => {
  return (dispatch: Function) => {
    return httpPost(`${base(number)}/files/${id}/rename`, { name })
      .then((file) => {
        dispatch({ type: t.RENAME_FILE, number, file })
        dispatch(setBanner('File renamed'))
        return file
      })
      .catch((err) => {
        Rollbar.error('Error on renameFile', err)
        dispatch(setBanner(`Error renaming file - ${err.message || err}`, 'danger'))
        throw err
      })
  }
}

export const restoreFile = (number: string, id: number) => {
  return (dispatch: Function) => {
    return httpPost(`${base(number)}/files/${id}/restore`, {})
      .then((file) => {
        dispatch({ type: t.RESTORE_FILE, number, file })
        dispatch(setBanner('File restored'))
      })
      .catch((err) => {
        Rollbar.error('Error on restoreFile', err)
        dispatch(setBanner(`Error restoring file - ${err.message || err}`, 'danger'))
      })
  }
}
